"use client";

import { useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import Image from "next/image";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { trackConversion } from "@/components/google-ads-tracking";
import { buildOnboardingStartUrl } from "@/lib/onboarding-constants";

export function SignupForm({
  className,
  ...props
}: React.ComponentProps<"div">) {
  const { signInWithGoogle, signUpWithEmail } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  const returnUrl = searchParams.get('returnUrl') || buildOnboardingStartUrl();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
  });

  const handleGoogleSignUp = async () => {
    try {
      setLoading(true);
      setError(""); // Clear any previous errors
      const { verified } = await signInWithGoogle();

      // Track Google Ads conversion
      if (process.env.NEXT_PUBLIC_GOOGLE_ADS_SIGNUP_LABEL) {
        trackConversion(process.env.NEXT_PUBLIC_GOOGLE_ADS_SIGNUP_LABEL, 1.0);
      }

      if (!verified) {
        router.push(`/verify-email?returnUrl=${encodeURIComponent(returnUrl)}`);
      } else {
        router.push(returnUrl);
      }
    } catch (error: any) {
      // Only show error message if it's not popup cancellation
      if (error?.code !== "auth/popup-closed-by-user") {
        setError(error?.message || "Google sign-up failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(""); // Clear any previous errors
      const { verified } = await signUpWithEmail(
        formData.email,
        formData.password,
        formData.name,
      );

      // Track Google Ads conversion
      if (process.env.NEXT_PUBLIC_GOOGLE_ADS_SIGNUP_LABEL) {
        trackConversion(process.env.NEXT_PUBLIC_GOOGLE_ADS_SIGNUP_LABEL, 1.0);
      }

      if (!verified) {
        router.push(`/verify-email?returnUrl=${encodeURIComponent(returnUrl)}`);
      } else {
        router.push(returnUrl);
      }
    } catch (error: any) {
      setError(error?.message || "Sign-up failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  return (
    <div className={cn("flex flex-col gap-5", className)} {...props}>
      <form onSubmit={handleEmailSignUp}>
        <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-6 sm:p-8 backdrop-blur-sm relative">
          {/* Emerald hairline along the top edge */}
          <div className="pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-emerald-400/50 to-transparent" />

          <div className="flex flex-col gap-6">
            <div className="flex flex-col items-center gap-1.5">
              <Link
                href="/"
                className="flex flex-col items-center font-medium"
              >
                <div className="flex items-center justify-center">
                  <Image
                    src="/logo.png"
                    alt="Xynth Logo"
                    width={88}
                    height={88}
                  />
                </div>
                <span className="sr-only">Xynth</span>
              </Link>
              <h1 className="text-2xl font-semibold tracking-tight text-white">Create your Xynth account</h1>
              <p className="text-sm text-white/80">
                Free to start. 20 Gemini messages a day, no card needed.
              </p>
              <div className="text-center text-sm text-white/80">
                Already have an account?{" "}
                <Link href={returnUrl !== '/' ? `/login?returnUrl=${encodeURIComponent(returnUrl)}` : '/login'} className="text-white underline underline-offset-4 hover:text-emerald-300">
                  Sign in
                </Link>
              </div>
            </div>
            <div className="flex flex-col gap-4">
              <div className="grid gap-2">
                <Label htmlFor="name" className="text-white/90">Full Name</Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="John Doe"
                  className="h-11 bg-white/[0.03] border-white/[0.10] focus-visible:border-emerald-400/50"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="email" className="text-white/90">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="m@example.com"
                  className="h-11 bg-white/[0.03] border-white/[0.10] focus-visible:border-emerald-400/50"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="password" className="text-white/90">Password</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="Password"
                  className="h-11 bg-white/[0.03] border-white/[0.10] focus-visible:border-emerald-400/50"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                />
              </div>
              {error && <div className="text-rose-400 text-sm">{error}</div>}
              <Button
                type="submit"
                className="w-full h-12 text-[15px] font-semibold bg-emerald-400 text-zinc-950 hover:bg-emerald-300 shadow-lg shadow-emerald-500/15"
                disabled={loading}
              >
                {loading ? "Creating account..." : "Create Free Account"}
              </Button>
            </div>
            <div className="after:border-white/[0.08] relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t">
              <span className="bg-[#0c0c0e] text-white/80 relative z-10 px-2">
                Or
              </span>
            </div>
            <Button
              variant="outline"
              type="button"
              className="w-full h-11 border-white/[0.12] bg-white/[0.02] text-white hover:bg-white/[0.06]"
              onClick={handleGoogleSignUp}
              disabled={loading}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                className="w-5 h-5 mr-2"
              >
                <path
                  d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"
                  fill="currentColor"
                />
              </svg>
              {loading ? "Creating account..." : "Continue with Google"}
            </Button>
          </div>
        </div>
      </form>
      <div className="text-white/70 text-center text-xs text-balance">
        By clicking continue, you agree to our{" "}
        <Link
          href="/terms"
          className="underline underline-offset-4 hover:text-white"
        >
          Terms of Service
        </Link>{" "}
        and{" "}
        <Link
          href="/privacy"
          className="underline underline-offset-4 hover:text-white"
        >
          Privacy Policy
        </Link>
        .
      </div>
    </div>
  );
}
